/**
 * Workshop version of ConnectionFrame — icon grid hub view.
 *
 * Shows all apps in a 4-column icon grid. Connected apps are bright with
 * a green status dot. Unconnected apps are dimmed; clicking one calls
 * onConnect(appId) to trigger the OAuth flow directly.
 */
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { FrameHeader } from "@/workshop/components/shapes/shared/frame-header";
import { FrameFooter } from "@/workshop/components/shapes/shared/frame-footer";
import { APP_BG, APP_ICON, APP_NAME } from "./app-icons";
import type { AppId } from "./app-icons";

interface ConnectedApp {
  id: AppId;
  connected: boolean;
}

interface ConnectionFrameProps {
  apps?: ConnectedApp[];
  title?: string;
  agentName?: string;
  onConnect?: (appId: AppId) => void;
  onBrowse?: () => void;
  onResizeStart?: React.MouseEventHandler;
  className?: string;
  "data-testid"?: string;
}

const DEFAULT_APPS: ConnectedApp[] = [
  { id: "gmail", connected: true },
  { id: "slack", connected: true },
  { id: "linkedin", connected: true },
  { id: "github", connected: false },
  { id: "notion", connected: false },
];

export function ConnectionFrame({
  apps = DEFAULT_APPS,
  title = "Connected Apps",
  agentName = "System",
  onConnect,
  onBrowse,
  onResizeStart,
  className,
  "data-testid": testId = "connection-frame",
}: ConnectionFrameProps) {
  const connectedCount = apps.filter((a) => a.connected).length;

  return (
    <div
      className={cn(
        "denker-glass-frame flex flex-col rounded-frame overflow-hidden",
        className,
      )}
      data-testid={testId}
    >
      <FrameHeader
        title={title}
        icon={Icons.Plug}
        accentColor="bg-frame-connection"
        agentName={agentName}
        statusColor="bg-accent"
        data-testid={`${testId}-header`}
      />

      <div className="flex flex-col gap-2 p-3">
        {/* Icon grid — 4 columns */}
        <div className="grid grid-cols-4 gap-2">
          {apps.map((app) => (
            <AppTile
              key={app.id}
              app={app}
              onConnect={onConnect}
              testId={testId}
            />
          ))}
          {/* Browse more button */}
          <button
            type="button"
            onClick={onBrowse}
            className="flex flex-col items-center gap-1 opacity-40 transition-opacity hover:opacity-70"
            data-testid={`${testId}-browse-btn`}
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] border border-dashed border-overlay-border bg-surface">
              <Icons.Plus className="h-3.5 w-3.5 text-muted" />
            </div>
            <span className="text-canvas-2xs text-muted">Browse</span>
          </button>
        </div>

        {/* Status */}
        <p className="text-center text-canvas-2xs text-muted">
          {connectedCount} of {apps.length} connected
        </p>
      </div>

      <FrameFooter
        onResizeStart={onResizeStart}
        data-testid={`${testId}-footer`}
      />
    </div>
  );
}

function AppTile({
  app,
  onConnect,
  testId,
}: {
  app: ConnectedApp;
  onConnect?: (id: AppId) => void;
  testId: string;
}) {
  const Icon = APP_ICON[app.id];
  const bg = APP_BG[app.id];
  const name = APP_NAME[app.id];

  return (
    <button
      type="button"
      onClick={() => onConnect?.(app.id)}
      className={cn(
        "flex flex-col items-center gap-1 transition-opacity",
        !app.connected && "opacity-30 hover:opacity-50",
      )}
      disabled={app.connected}
      data-testid={`${testId}-app-${app.id}`}
    >
      <div className="relative">
        <div
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-[10px]",
            app.connected ? "shadow-sm" : "border border-dashed border-overlay-border",
            bg,
          )}
        >
          <Icon size={20} />
        </div>
        {app.connected && (
          <span
            className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-accent ring-2 ring-glass-fill"
            aria-label="Connected"
          />
        )}
      </div>
      <span className={cn("text-canvas-2xs", app.connected ? "text-secondary" : "text-muted")}>
        {name}
      </span>
    </button>
  );
}

export type { ConnectionFrameProps, ConnectedApp };
